import React, { useState, useContext } from 'react';
import {
  InputField,
  SelectField,
  CheckboxField
} from '@trp-beacon-ui-kit/react-controls';
import { states } from '../FONASUCustomerInformation/util';
import styles from './FONASUCustomerInformationSection.css';
import { FlowContext } from '../../core/flow-engine';
import {
  formatSsnForDisplay,
  provideDefault,
  handleInputFormat
} from '../../flows/shared/utils';
import {
  stateValidation,
  addressLine1Validation,
  poBoxLine1Validation,
  addressLine2Validation,
  poBoxLine2Validation,
  cityValidation,
  zipCodeValidation,
  emailValidation,
  phoneValidation,
  mailingStateValidation,
  mailingCityValidation,
  middlenameValidation,
  firstnameValidation,
  lastnameValidation
} from '../../flows/shared/validations';
import { withDefault } from '../../core/util';

export const MailingAddressSection = ({ mailingAddress }) => {
  const { formProps } = useContext(FlowContext);

  const {
    street1: mailingStreet1,
    street2: mailingStreet2,
    city: mailingCity,
    state: mailingState,
    zip: mailingZip
  } = mailingAddress;

  return (
    <>
      <div className="mailing-address-wrapper">
        <div className="qv-row">
          <div className="qv-col">
            <InputField
              title="Mailing Address"
              className="mailing-address-street-1"
              {...formProps.mailingStreet1}
              defaultValue={provideDefault(
                mailingStreet1,
                formProps.mailingStreet1
              )}
              validation={addressLine1Validation}
              maxLength="35"
            />
          </div>
          <div className="qv-col">
            <InputField
              title="Address Line 2"
              {...formProps.mailingStreet2}
              defaultValue={provideDefault(
                mailingStreet2,
                formProps.mailingStreet2
              )}
              validation={addressLine2Validation}
              maxLength="35"
            />
          </div>
        </div>
        <div className="qv-row">
          <div className="qv-col">
            <InputField
              title="City"
              {...formProps.mailingCity}
              defaultValue={provideDefault(mailingCity, formProps.mailingCity)}
              validation={mailingCityValidation}
            />
          </div>
          <div className="qv-col">
            <InputField
              title="Zip Code"
              {...formProps.mailingZip}
              defaultValue={provideDefault(mailingZip, formProps.mailingZip)}
              validation={zipCodeValidation}
            />
          </div>
          <div className="qv-col">
            <SelectField
              title="State"
              {...formProps.mailingState}
              defaultValue={provideDefault(
                mailingState,
                formProps.mailingState
              )}
              validation={mailingStateValidation}>
              <option hidden value="">
                State
              </option>
              {states.map(item => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </SelectField>
          </div>
        </div>
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

const FONASUCustomerInformationSection = ({
  phone1,
  phone2,
  phone3,
  mailingAddressDifferent,
  phoneNumberRequired,
  setPhoneNumberRequired
}) => {
  const {
    formProps,
    appState: {
      store: {
        app: { customer }
      }
    }
  } = useContext(FlowContext);

  const {
    name,
    taxID,
    citizenship,
    primaryEmailAddress,
    dob,
    residentialAddress,
    mailingAddress
  } = customer;

  const { firstName, middleName, lastName } = name;
  const { street1, street2, city, state, zip } = residentialAddress;

  const [shouldShowMailingAddress, setShouldShowMailingAddress] = useState(
    mailingAddressDifferent
  );

  const [mailingAddressInputs, setMailingAddressInputs] = useState(
    mailingAddress
  );

  const [mobilePhoneInput, setMobilePhoneInput] = useState(phone1);
  const [dayTimePhoneInput, setDayTimePhoneInput] = useState(phone2);
  const [eveningPhoneInput, setEveningPhoneInput] = useState(phone3);

  const [dobValue, setDobValue] = useState(withDefault(dob, ''));
  const [ssnValue, setSSNValue] = useState(
    withDefault(formatSsnForDisplay(taxID), '')
  );
  const [phone1Value, setPhone1Value] = useState('');
  const [phone2Value, setPhone2Value] = useState('');
  const [phone3Value, setPhone3Value] = useState('');

  const toggleMailingAddressDifferent = () => {
    setMailingAddressInputs({
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip: ''
    });
    setShouldShowMailingAddress(!shouldShowMailingAddress);
  };

  const handlePhoneInput1 = ({ target: { value } }) => {
    if (value) {
      setMobilePhoneInput(value);
      return setPhoneNumberRequired(false);
    }
    if (!(dayTimePhoneInput || eveningPhoneInput)) {
      setMobilePhoneInput('');
      return setPhoneNumberRequired(true);
    }
    return setMobilePhoneInput('');
  };

  const handlePhoneInput2 = ({ target: { value } }) => {
    if (value) {
      setDayTimePhoneInput(value);
      return setPhoneNumberRequired(false);
    }
    if (!(mobilePhoneInput || eveningPhoneInput)) {
      setDayTimePhoneInput('');
      setPhoneNumberRequired(true);
    }
    return setDayTimePhoneInput('');
  };

  const handlePhoneInput3 = ({ target: { value } }) => {
    if (value) {
      setEveningPhoneInput(value);
      return setPhoneNumberRequired(false);
    }
    if (!(mobilePhoneInput || dayTimePhoneInput)) {
      setEveningPhoneInput('');
      return setPhoneNumberRequired(true);
    }

    return setEveningPhoneInput('');
  };

  return (
    <div className="review-section">
      <div className="residential-address-wrapper">
        <div className="qv-row">
          <div className="qv-col">
            <InputField
              type="text"
              title="First Name"
              {...formProps.firstName}
              defaultValue={firstName}
              maxLength="35"
              validation={firstnameValidation}
            />
          </div>
          <div className="mn-col">
            <InputField
              type="text"
              title="Middle Name"
              {...formProps.middleName}
              defaultValue={middleName}
              validation={middlenameValidation}
            />
          </div>
          <div className="qv-col">
            <InputField
              type="text"
              title="Last Name"
              {...formProps.lastName}
              defaultValue={lastName}
              maxLength="35"
              validation={lastnameValidation}
            />
          </div>
        </div>
        <div className="qv-row">
          <div className="qv-col">
            <InputField
              type="text"
              title="Date of Birth"
              onChange={evt => handleInputFormat(evt, 'DATE', setDobValue)}
              placeholder="mm/dd/yyyy"
              maxLength="10"
              {...formProps.dob}
              value={dobValue}
            />
          </div>
          <div className="qv-col">
            <InputField
              title="Social Security Number"
              placeholder="000-00-0000"
              maxLength="11"
              onChange={evt => handleInputFormat(evt, 'SSN', setSSNValue)}
              {...formProps.ssn}
              value={ssnValue}
            />
          </div>
          <div className="qv-col">
            <SelectField
              title="Citizenship"
              {...formProps.citizenship}
              defaultValue={citizenship}>
              <option value="US_CITIZEN">U.S. Citizen</option>
              <option value="RESIDENT_ALIEN">U.S. Resident Alien</option>
            </SelectField>
          </div>
        </div>
        <div className="qv-row">
          <div className="qv-col">
            <InputField
              title="Address Line 1"
              {...formProps.residentialStreet1}
              defaultValue={provideDefault(
                street1,
                formProps.residentialStreet1
              )}
              validation={[addressLine1Validation, poBoxLine1Validation]}
              maxLength="35"
            />
          </div>
          <div className="qv-col">
            <InputField
              title="Address Line 2"
              {...formProps.residentialStreet2}
              defaultValue={provideDefault(
                street2,
                formProps.residentialStreet2
              )}
              validation={[addressLine2Validation, poBoxLine2Validation]}
              maxLength="35"
            />
          </div>
        </div>
        <div className="qv-row">
          <div className="qv-col">
            <InputField
              title="City"
              {...formProps.residentialCity}
              defaultValue={provideDefault(city, formProps.residentialCity)}
              validation={cityValidation}
            />
          </div>
          <div className="qv-col">
            <InputField
              title="Zip Code"
              {...formProps.residentialZip}
              defaultValue={provideDefault(zip, formProps.residentialZip)}
              validation={zipCodeValidation}
            />
          </div>
          <div className="qv-col">
            <SelectField
              title="State"
              {...formProps.residentialState}
              defaultValue={provideDefault(state, formProps.residentialState)}
              validation={stateValidation}>
              <option hidden value="">
                State
              </option>
              {states.map(item => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </SelectField>
          </div>
        </div>
      </div>

      <CheckboxField
        name="mailingAddressDifferent"
        title="My mailing address is different from my residential address"
        onClick={toggleMailingAddressDifferent}
        {...formProps.mailingAddressDifferent}
        checked={shouldShowMailingAddress}
      />

      {shouldShowMailingAddress && (
        <MailingAddressSection mailingAddress={mailingAddressInputs} />
      )}
      <div className="qv-row contact-fields">
        <div className="qv-col">
          <InputField
            type="email"
            title="Email Address"
            {...formProps.primaryEmailAddress}
            defaultValue={provideDefault(
              primaryEmailAddress,
              formProps.primaryEmailAddress
            )}
            validation={emailValidation}
          />
        </div>
        <div className="qv-col">
          <InputField
            title="Mobile Phone Number"
            name="phone1"
            placeholder="000-000-0000"
            maxLength="14"
            {...formProps.phone1}
            value={withDefault(phone1Value, phone1)}
            onChange={evt => {
              handlePhoneInput1(evt);
              handleInputFormat(evt, 'PHONE', setPhone1Value);
            }}
            validation={phoneValidation}
          />
        </div>
      </div>
      <div className="qv-row contact-fields">
        <div className="qv-col">
          <InputField
            title="Daytime Phone Number"
            name="phone2"
            placeholder="000-000-0000"
            maxLength="14"
            {...formProps.phone2}
            value={withDefault(phone2Value, phone2)}
            onChange={evt => {
              handlePhoneInput2(evt);
              handleInputFormat(evt, 'PHONE', setPhone2Value);
            }}
            validation={phoneValidation}
          />
        </div>
        <div className="qv-col">
          <InputField
            title="Evening Phone Number"
            name="phone3"
            placeholder="000-000-0000"
            maxLength="14"
            {...formProps.phone3}
            value={withDefault(phone3Value, phone3)}
            onChange={evt => {
              handlePhoneInput3(evt);
              handleInputFormat(evt, 'PHONE', setPhone3Value);
            }}
            validation={phoneValidation}
          />
        </div>
      </div>
      {phoneNumberRequired && (
        <div className="phone-error-message">
          Enter at least one phone number
        </div>
      )}
      <style jsx>{styles}</style>
    </div>
  );
};

export default FONASUCustomerInformationSection;
